import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, LoaderCircle } from "lucide-react";
import { addDays, clinicMinutesOfDay, formatClinicDate, formatClinicTime, type Practitioner, type Slot } from "@cop/domain";
import { api } from "../lib/api";
import { errorMessage } from "../lib/firebase";

export function SlotChooser({
  serviceId,
  practitionerId,
  date,
  onDate,
  value,
  onChange,
  practitioners,
}: {
  serviceId: string;
  practitionerId: string;
  date: string;
  onDate: (d: string) => void;
  value: Slot | null;
  onChange: (s: Slot) => void;
  practitioners: Practitioner[];
}) {
  const [slots, setSlots] = useState<Slot[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!serviceId) return;
    let alive = true;
    setSlots(null);
    setError(null);
    api
      .availability({ serviceId, practitionerId: practitionerId || undefined, from: date, days: 1 })
      .then((r) => alive && setSlots(r.days[0]?.slots ?? []))
      .catch((e) => alive && (setError(errorMessage(e)), setSlots([])));
    return () => {
      alive = false;
    };
  }, [serviceId, practitionerId, date]);

  const name = (id: string) => practitioners.find((p) => p.id === id)?.displayName ?? id;

  return (
    <div className="slot-chooser">
      <div className="row gap-8 center">
        <button type="button" className="btn btn-sm" onClick={() => onDate(addDays(date, -1))} aria-label="Día anterior"><ChevronLeft size={16} aria-hidden />
        </button>
        <input type="date" value={date} onChange={(e) => e.target.value && onDate(e.target.value)} aria-label="Fecha" />
        <button type="button" className="btn btn-sm" onClick={() => onDate(addDays(date, 1))} aria-label="Día siguiente"><ChevronRight size={16} aria-hidden />
        </button>
        <span className="muted small cap">{formatClinicDate(`${date}T15:00:00Z`)}</span>
      </div>
      {error && <p className="field-error">{error}</p>}
      {slots === null ? (
        <p className="muted small row gap-8 center">
          <LoaderCircle className="spin" size={14} aria-hidden /> Buscando horarios…
        </p>
      ) : slots.length === 0 ? (
        <p className="muted small">Sin horarios libres ese día para esta selección.</p>
      ) : (
        <div className="slot-grid" role="radiogroup" aria-label="Horarios disponibles">
          {slots.map((s) => {
            const on = value?.start === s.start && value.practitionerId === s.practitionerId;
            return (
              <button
                type="button"
                role="radio"
                aria-checked={on}
                key={`${s.start}-${s.practitionerId}`}
                className="slot-btn"
                data-pm={clinicMinutesOfDay(new Date(s.start)) >= 780 || undefined}
                onClick={() => onChange(s)}
              >
                <span className="mono">{formatClinicTime(s.start)}</span>
                {!practitionerId && <span className="slot-who">{name(s.practitionerId)}</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
